import cn from 'classnames';
import { lazy, useEffect, useState } from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';

import { Fee, SupportedCountry, useCheckoutContext } from '@zooplus/checkout-state';
import { Check } from '@zooplus/icons';
import { Anchor, H4, P1 } from '@zooplus/ui-components';

import { useTranslation } from '@hooks/use-translations';
import { CART_VERSION, CHECKOUT_STATE_PROJECT_ID, FEE_TYPE, PAGE_TYPE } from '@utilities/constants';
import { isArrayNotEmpty } from '@utilities/utils';

import styles from './cart-shipping-fees-popover.module.scss';
import ShippingCountryAndZipForm from './shipping-country-and-zip-form';

const LazyArrowAngleDown = lazy(() =>
  import('@zooplus/icons').then(({ ArrowAngleDown }) => ({ default: ArrowAngleDown })),
);

const CartShippingFeesPopover = () => {
  const { t } = useTranslation();
  const {
    checkoutState,
    mainTogglesMap = {},
    pageType,
    cartVersion,
  } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  let selectedDeliveryCountry = checkoutState?.cart?.selectedDeliveryCountry || '';
  let deliveryZipCode = checkoutState?.cart?.deliveryZipCode || '';
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
  const isNewPreviewPage = mainTogglesMap.CHECKOUT_FRONTEND_NEW_PREVIEW_PAGE;

  if (isCartApiV2) {
    deliveryZipCode = checkoutState?.cart?.deliveryDetails?.deliveryZipCode || '';
    selectedDeliveryCountry = checkoutState?.cart?.deliveryDetails?.selectedDeliveryCountry || '';
  }
  const fees = checkoutState?.cart?.fees;
  const disableEditDeliveryCountry = checkoutState?.stateAttributes?.disableEditDeliveryCountry;
  const supportedCountries = checkoutState?.stateAttributes?.supportedCountries || [];

  const [shippingFee, setShippingFee] = useState<number>(0);
  const [logisticFee, setLogisticFee] = useState<number>(0);
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [shippingFeeValue, setShippingFeeValue] = useState<string>();
  const [formattedShippingFee, setFormattedShippingFee] = useState<string>();

  const isPreviewPage: boolean = isNewPreviewPage && pageType === PAGE_TYPE.PREVIEW;
  const isShippingFree: boolean = shippingFee === 0 && logisticFee === 0;
  const shippingFeeValueMessage: string = t('cart.summary.shippingFees.freeShipping');
  const singleDeliveryCountry =
    isArrayNotEmpty(supportedCountries as SupportedCountry[]) && supportedCountries.length === 1;

  useEffect(() => {
    if (isArrayNotEmpty(fees as Fee[])) {
      setLogisticFee(0);
      fees?.forEach((fee) => {
        if (fee.type === FEE_TYPE.SHIPPING) {
          setShippingFee(fee.subtotal);
          setFormattedShippingFee(fee.formattedSubtotal);
        }
        if (fee.type === FEE_TYPE.LOGISTICS) {
          setLogisticFee(fee.subtotal);
        }
      });
    }
  }, [fees]);

  useEffect(() => {
    if (isShippingFree) {
      setShippingFeeValue(shippingFeeValueMessage);
    } else {
      setShippingFeeValue(formattedShippingFee);
    }
  }, [formattedShippingFee, shippingFeeValueMessage, isShippingFree]);

  const getZipCode = () => (deliveryZipCode ? `(${deliveryZipCode})` : '');

  const getCountryName = () => {
    const matchingCountry = supportedCountries?.find(
      (country) => country?.countryCode === selectedDeliveryCountry,
    );
    return matchingCountry?.countryName || getFallbackCountryName();
  };

  const getFallbackCountryName = () => supportedCountries[0]?.countryName;

  const renderShippingCountryLabelWithPopover = () => {
    return (
      <div className={styles.shippingCountryWrapper}>
        <Anchor
          className={styles.shippingCountryLabel}
          data-testid="shipping-country-name"
          tabIndex={0}
          isBold
          onClick={() => setPopoverOpen(true)}>
          {`${getCountryName()} ${getZipCode()}`}
          <LazyArrowAngleDown size={16} aria-hidden="true" />
        </Anchor>
      </div>
    );
  };

  const renderShippingCountryLabelWithoutPopover = () => {
    return (
      <P1
        className={styles.shippingCountryWithoutPopover}
        data-testid="shipping-country-name">{` ${getCountryName()} ${getZipCode()}`}</P1>
    );
  };

  const renderShippingCountryPopover = () => {
    return (
      <Popover
        containerClassName={styles.popoverWrapper}
        isOpen={isPopoverOpen}
        positions={['left', 'top']}
        reposition
        padding={5}
        onClickOutside={() => setPopoverOpen(false)}
        content={({ position, popoverRect, childRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor={'white'}
            arrowSize={7}>
            <ShippingCountryAndZipForm
              popoverVisibility={setPopoverOpen}
              deliveryZipCode={deliveryZipCode}
              supportedCountries={supportedCountries}
              selectedDeliveryCountry={selectedDeliveryCountry}
            />
          </ArrowContainer>
        )}>
        {renderShippingCountryLabelWithPopover()}
      </Popover>
    );
  };

  return (
    <div className={styles.shippingFeesWrapper}>
      <H4>{t('cart.summary.shippingFees')}</H4>
      <div className={styles.deliverySelection}>
        <div
          className={`${disableEditDeliveryCountry && styles.disabledPopoverShippingCostWrapper} ${styles.deliverySelectionLabel}`}>
          {!singleDeliveryCountry && !isPreviewPage && <P1>{t('cart.summary.deliveryTo')}</P1>}
          {!disableEditDeliveryCountry && !singleDeliveryCountry ? (
            renderShippingCountryPopover()
          ) : !isPreviewPage ? (
            renderShippingCountryLabelWithoutPopover()
          ) : (
            <P1>{t('cart.summary.shippingFees')}</P1>
          )}
        </div>
        <P1
          data-testid="shipping-cost-value-overview"
          className={cn(
            styles.shippingCostValue,
            isPreviewPage && isShippingFree && styles.freeShipping,
          )}>
          {isPreviewPage && isShippingFree && <Check />}
          {shippingFeeValue}
        </P1>
      </div>
    </div>
  );
};

export default CartShippingFeesPopover;
